import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const AccountSetupForm = ({ formData, onUpdate, onNext, onPrevious, onSubmit, authError }) => {
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handlePreferenceToggle = (preference) => {
    const current = formData.preferences || [];
    const updated = current.includes(preference)
      ? current.filter(p => p !== preference)
      : [...current, preference];
    
    handleInputChange('preferences', updated);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password?.trim()) newErrors.password = 'Password is required';
    if (!formData.confirmPassword?.trim()) newErrors.confirmPassword = 'Please confirm your password';
    
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit();
    }
  };

  const preferences = [
    { id: 'email-updates', label: 'Email Updates', description: 'Get planning tips and vendor updates' },
    { id: 'sms-reminders', label: 'SMS Reminders', description: 'Important deadline reminders' },
    { id: 'vendor-recommendations', label: 'Vendor Recommendations', description: 'Personalized vendor suggestions' },
    { id: 'planning-tips', label: 'Planning Tips', description: 'Weekly planning advice and articles' },
    { id: 'exclusive-offers', label: 'Exclusive Offers', description: 'Special discounts from vendors' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Account Setup
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Create your password and customize your preferences
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Password *
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Create a strong password"
                value={formData.password || ''}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className={errors.password ? 'border-error pr-10' : 'pr-10'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-muted hover:text-text-secondary"
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} color="currentColor" />
              </button>
            </div>
            {errors.password && (
              <p className="text-error text-sm font-caption mt-1">{errors.password}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Confirm Password *
            </label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                value={formData.confirmPassword || ''}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                className={errors.confirmPassword ? 'border-error pr-10' : 'pr-10'}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-muted hover:text-text-secondary"
              >
                <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={16} color="currentColor" />
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-error text-sm font-caption mt-1">{errors.confirmPassword}</p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <div className="bg-surface-50 rounded-lg p-4">
            <h4 className="font-medium text-sm text-text-primary mb-2">Password Requirements:</h4>
            <ul className="text-sm text-text-secondary space-y-1">
              <li className="flex items-center space-x-2">
                <Icon name={formData.password?.length >= 6 ? 'CheckCircle' : 'Circle'} size={14} color={formData.password?.length >= 6 ? '#228B22' : '#8B7B6F'} />
                <span>At least 6 characters</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name={/[A-Z]/.test(formData.password) ? 'CheckCircle' : 'Circle'} size={14} color={/[A-Z]/.test(formData.password) ? '#228B22' : '#8B7B6F'} />
                <span>One uppercase letter (recommended)</span>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name={/[0-9]/.test(formData.password) ? 'CheckCircle' : 'Circle'} size={14} color={/[0-9]/.test(formData.password) ? '#228B22' : '#8B7B6F'} />
                <span>One number (recommended)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Communication Preferences
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Choose how you'd like to receive updates and recommendations
        </p>

        <div className="space-y-4">
          {preferences.map((preference) => {
            const isSelected = formData.preferences?.includes(preference.id);
            
            return (
              <div key={preference.id} className="flex items-start space-x-3">
                <button
                  onClick={() => handlePreferenceToggle(preference.id)}
                  className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-smooth ${
                    isSelected
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'border-border hover:border-primary'
                  }`}
                >
                  {isSelected && (
                    <Icon name="Check" size={12} color="currentColor" />
                  )}
                </button>
                <div className="flex-1">
                  <h4 className="font-medium text-sm text-text-primary">
                    {preference.label}
                  </h4>
                  <p className="text-xs text-text-muted font-caption">
                    {preference.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {authError && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} color="#DC2626" />
            <span className="text-sm text-error font-caption">{authError}</span>
          </div>
        </div>
      )}

      <div className="bg-surface-50 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} color="#8B4513" />
          <div className="flex-1">
            <h4 className="font-medium text-sm text-text-primary mb-2">
              Privacy & Terms
            </h4>
            <p className="text-xs text-text-secondary font-caption mb-3">
              By creating an account, you agree to our Terms of Service and Privacy Policy. 
              Your personal information is encrypted and secure.
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleInputChange('agreeToTerms', !formData.agreeToTerms)}
                className={`flex items-center justify-center w-4 h-4 rounded border-2 transition-smooth ${
                  formData.agreeToTerms
                    ? 'bg-primary border-primary text-primary-foreground'
                    : 'border-border hover:border-primary'
                }`}
              >
                {formData.agreeToTerms && (
                  <Icon name="Check" size={10} color="currentColor" />
                )}
              </button>
              <span className="text-xs text-text-secondary">
                I agree to the Terms of Service and Privacy Policy
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t border-border">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconSize={18}
        >
          Previous
        </Button>
        
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          iconName="UserPlus"
          iconPosition="right"
          iconSize={18}
          disabled={!formData.agreeToTerms}
        >
          Create Account
        </Button>
      </div>
    </div>
  );
};

export default AccountSetupForm;